<template>
  <div
    ref="gridRef"
    class="year-grid"
    :data-testid="`${testid}-custom-picker-year`"
  >
    <v-list class="year-list-items">
      <v-list-item
        v-for="year in allowedYears"
        :key="year"
        class="year-list-item"
        :class="{
          selected: year === selected,
          current: year === currentYear,
        }"
        @click="$emit('select', year)"
      >
        {{ year }}
      </v-list-item>
    </v-list>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, onMounted, nextTick, watch } from 'vue'

export default defineComponent({
  name: 'YearGrid',
  props: {
    allowedYears: {
      type: Array as PropType<number[]>,
      required: true,
    },
    selected: {
      type: Number,
      required: true,
    },
    testid: {
      type: String,
      required: true,
    },
  },
  emits: ['select'],
  setup(props) {
    const pickerOpen = ref(false);
    const currentYear = new Date().getFullYear();
    const gridRef = ref<HTMLElement | null>(null);

    const scrollToSelected = async () => {
      await nextTick();

      const container = gridRef.value?.querySelector('.year-list-items') as HTMLElement | null;
      if (!container) return;

      const index = props.allowedYears.indexOf(props.selected);
      if (index === -1) return;

      const columns = 3;
      const yearListItemHeight = 36;
      const gapRem = 0.45;
      const gapPx = gapRem * parseFloat(getComputedStyle(document.documentElement).fontSize)

      const row = Math.floor(index / columns);
      const scrollTop = row * (yearListItemHeight + gapPx) - container.clientHeight / 2 + yearListItemHeight / 2;

      container.scrollTop = Math.max(scrollTop, 0);
    }

    onMounted(scrollToSelected);

    watch(pickerOpen, scrollToSelected);

    return {
      currentYear,
      gridRef,
    }
  }
})
</script>

<style scoped>
.year-grid {
  width: 350px;
  height: 290px;
}

.year-list-items {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.45rem;
  padding: 0 25px;
  list-style: none;
  max-height: 285px;
  overflow-y: auto;
}

.year-list-item {
  font-size: 0.875rem;
  height: 36px !important;
  min-height: 36px !important;
  max-height: 36px !important;
  width: 85%;
  margin: 0 auto;
  cursor: pointer;
  text-align: center;
  padding: 0.5rem 0;
  transition: background 0.2s;
}

.year-list-item:hover {
  background-color: rgb(var(--v-theme-primary) / 0.1);
}

.year-list-item.selected {
  background-color: rgb(var(--v-theme-primary));
  color: white;
}

.year-list-item.current {
  border: 1px solid rgb(var(--v-theme-primary));
  box-sizing: border-box;
}
</style>